import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Link2 } from "lucide-react";
import { sumsubHub } from "@/lib/api";

export const ANSWER_TONE = {
  GREEN: "bg-emerald-100 text-emerald-800 border-emerald-300",
  RED: "bg-red-100 text-red-900 border-red-300",
  YELLOW: "bg-amber-100 text-amber-900 border-amber-300",
  none: "bg-slate-100 text-slate-600 border-slate-300",
};
export const STATUS_TONE = {
  completed: "bg-slate-100 text-slate-700 border-slate-300",
  init: "bg-sky-50 text-sky-800 border-sky-300",
  pending: "bg-violet-50 text-violet-800 border-violet-300",
  queued: "bg-violet-50 text-violet-800 border-violet-300",
  prechecked: "bg-violet-50 text-violet-800 border-violet-300",
  onHold: "bg-amber-50 text-amber-900 border-amber-300",
};
export const STATUS_LABEL = { completed: "Completed", init: "Started", pending: "Pending review", queued: "Queued", prechecked: "Pre-checked", onHold: "On hold" };
export const ATTENTION = {
  approved: { label: "Approved", tone: "bg-emerald-600 text-white border-emerald-600" },
  rejected: { label: "Rejected", tone: "bg-red-600 text-white border-red-600" },
  retry: { label: "Failed attempt", tone: "bg-orange-500 text-white border-orange-500" },
  on_hold: { label: "On hold", tone: "bg-amber-500 text-white border-amber-500" },
};

export const AnswerPill = ({ answer, testId, className = "" }) => (
  <Badge variant="outline" className={`text-[10px] font-bold tracking-wide px-1.5 h-4 ${ANSWER_TONE[answer] || ANSWER_TONE.none} ${className}`} data-testid={testId}>
    {answer || "—"}
  </Badge>
);
export const StatusPill = ({ status, testId }) => (
  <Badge variant="outline" className={`text-[10px] font-medium px-1.5 h-4 whitespace-nowrap ${STATUS_TONE[status] || STATUS_TONE.completed}`} data-testid={testId}>
    {STATUS_LABEL[status] || status || "—"}
  </Badge>
);
export const LabelPill = ({ children, tone = "slate" }) => (
  <Badge variant="outline" className={`text-[9px] font-mono px-1 h-4 ${tone === "red" ? "bg-rose-50 text-rose-700 border-rose-200" : tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
    {children}
  </Badge>
);
export const AttentionPill = ({ kind, unseen = true, testId }) => {
  const a = ATTENTION[kind];
  if (!a) return null;
  return (
    <Badge variant="outline" className={`text-[9px] font-semibold px-1.5 h-4 whitespace-nowrap ${unseen ? a.tone : "bg-white text-slate-500 border-slate-300"}`} data-testid={testId}>
      {unseen && <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-white/90" />}{a.label}
    </Badge>
  );
};
// Confirmed link (green) or fuzzy suggestion (amber + score) to an SF application.
export const LinkPill = ({ linked, suggested, testId, onClick }) => {
  if (linked?.sf) {
    return (
      <button type="button" onClick={onClick} className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-1.5 h-5 text-[10px] font-mono font-semibold text-emerald-800 hover:bg-emerald-100" title={`Attached to ${linked.sf}${linked.name ? ` · ${linked.name}` : ""}${linked.by ? ` · by ${linked.by}` : ""}`} data-testid={testId}>
        <Link2 className="w-3 h-3" />{linked.sf}
      </button>
    );
  }
  if (suggested?.sf) {
    return (
      <button type="button" onClick={onClick} className={`inline-flex items-center gap-1 rounded border px-1.5 h-5 text-[10px] font-mono hover:bg-amber-100 ${suggested.score >= 88 ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-500"}`} title={`Looks like ${suggested.sf} · ${suggested.name} (${suggested.score}% match) — click to attach`} data-testid={testId}>
        {suggested.sf}<span className="font-sans text-[9px] opacity-70">{suggested.score}%</span>
      </button>
    );
  }
  return <button type="button" onClick={onClick} className="text-[10px] text-slate-400 hover:text-slate-700 underline-offset-2 hover:underline" data-testid={testId}>attach…</button>;
};

export const fmtTs = (s) => {
  if (!s) return "—";
  const d = new Date(String(s).replace(" ", "T").replace(/\+0000$/, "Z"));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
export const fmtDate = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join("/") : "—");
export const nice = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export const openBlob = (blob, filename, download) => {
  const url = URL.createObjectURL(blob);
  if (download) {
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  } else {
    window.open(url, "_blank", "noopener");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Images need the bearer token, so they come through axios as blobs.
export function AuthImg({ applicantId, imageId, inspectionId, preview = true, alt = "", className = "", testId, onClick }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url;
    let alive = true;
    sumsubHub.imageBlob(applicantId, imageId, { inspectionId, preview })
      .then((blob) => { if (alive) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => alive && setErr(true));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [applicantId, imageId, inspectionId, preview]);
  if (err) return <div className={`flex items-center justify-center bg-slate-100 text-[10px] text-slate-400 ${className}`} data-testid={testId}>no preview</div>;
  if (!src) return <div className={`animate-pulse bg-slate-100 ${className}`} data-testid={testId} />;
  return <img src={src} alt={alt} className={className} data-testid={testId} onClick={onClick} />;
}
